import { useTranslation } from 'react-i18next';

import { SystemMessageBlock, SystemMessageBlockKind, SystemMessageButton } from '@/models/in/SystemMessageDtoIn';
import { MAX_BLOCKS, MAX_BULLET_ITEMS, MAX_BUTTONS, emptyBlock } from './systemMessages';
import styles from './SystemMessageBlocksEditor.module.scss';

interface SystemMessageBlocksEditorProps {
  blocks: SystemMessageBlock[];
  onChange: (blocks: SystemMessageBlock[]) => void;
}

const ADDABLE_KINDS: SystemMessageBlockKind[] = ['paragraph', 'heading', 'bullets', 'callout', 'buttons'];

/**
 * The builder an admin composes a message's body with: paragraphs, sub-headings, bullet lists,
 * highlighted notes and buttons, added and reordered one block at a time.
 *
 * Blocks rather than a rich-text box, because every one of them is drawn by the site's own
 * components - so an admin cannot land on a message that looks nothing like the rest of the site,
 * and nothing they type is ever interpreted as markup.
 */
const SystemMessageBlocksEditor = ({ blocks, onChange }: SystemMessageBlocksEditorProps) => {
  const { t } = useTranslation();

  const replaceAt = (index: number, block: SystemMessageBlock) => onChange(blocks.map((existing, i) => (i === index ? block : existing)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;

    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = (index: number) => onChange(blocks.filter((_, i) => i !== index));

  const add = (kind: SystemMessageBlockKind) => onChange([...blocks, emptyBlock(kind)]);

  return (
    <div className={styles.editor}>
      {/* No sentence for an empty list: the add buttons under it say what can be added. */}
      {blocks.length > 0 && (
        <div className={styles.blockList}>
          {blocks.map((block, index) => (
            <div key={index} className={styles.block}>
              <div className={styles.blockHeader}>
                <span className={`${styles.blockKind} body-sm-semibold`}>{t(`systemMessages.editor.kinds.${block.kind}`)}</span>
                <div className={styles.blockTools}>
                  <button
                    type="button"
                    className={styles.toolButton}
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={t('systemMessages.editor.moveUp')}
                  >
                    <Chevron direction="up" />
                  </button>
                  <button
                    type="button"
                    className={styles.toolButton}
                    onClick={() => move(index, 1)}
                    disabled={index === blocks.length - 1}
                    aria-label={t('systemMessages.editor.moveDown')}
                  >
                    <Chevron direction="down" />
                  </button>
                  <button
                    type="button"
                    className={`${styles.toolButton} ${styles.removeButton}`}
                    onClick={() => remove(index)}
                    aria-label={t('systemMessages.editor.removeBlock')}
                  >
                    <TrashGlyph />
                  </button>
                </div>
              </div>

              <BlockFields block={block} onChange={(updated) => replaceAt(index, updated)} />
            </div>
          ))}
        </div>
      )}

      <div className={styles.addRow}>
        {ADDABLE_KINDS.map((kind) => (
          <button key={kind} type="button" className={`${styles.addButton} body-sm-semibold`} onClick={() => add(kind)} disabled={blocks.length >= MAX_BLOCKS}>
            <PlusGlyph />
            {t(`systemMessages.editor.kinds.${kind}`)}
          </button>
        ))}
      </div>
    </div>
  );
};

const BlockFields = ({ block, onChange }: { block: SystemMessageBlock; onChange: (block: SystemMessageBlock) => void }) => {
  const { t } = useTranslation();

  switch (block.kind) {
    case 'heading':
      return (
        <input
          type="text"
          className={`${styles.field} body-sm-regular`}
          value={block.text ?? ''}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          placeholder={t('systemMessages.editor.headingPlaceholder')}
        />
      );

    case 'bullets': {
      const items = block.items ?? [];

      const setItem = (index: number, value: string) => onChange({ ...block, items: items.map((item, i) => (i === index ? value : item)) });

      return (
        <>
          {items.map((item, index) => (
            <div key={index} className={styles.itemRow}>
              <span className={styles.itemDot} aria-hidden="true" />
              <input
                type="text"
                className={`${styles.field} body-sm-regular`}
                value={item}
                onChange={(e) => setItem(index, e.target.value)}
                placeholder={t('systemMessages.editor.bulletPlaceholder')}
              />
              <button
                type="button"
                className={`${styles.toolButton} ${styles.removeButton}`}
                onClick={() => onChange({ ...block, items: items.filter((_, i) => i !== index) })}
                aria-label={t('systemMessages.editor.removeLine')}
              >
                <TrashGlyph />
              </button>
            </div>
          ))}
          <button
            type="button"
            className={`${styles.addButton} body-sm-semibold`}
            onClick={() => onChange({ ...block, items: [...items, ''] })}
            disabled={items.length >= MAX_BULLET_ITEMS}
          >
            <PlusGlyph />
            {t('systemMessages.editor.addLine')}
          </button>
        </>
      );
    }

    case 'buttons': {
      const buttons = block.buttons ?? [];

      const setButton = (index: number, updated: SystemMessageButton) =>
        onChange({ ...block, buttons: buttons.map((button, i) => (i === index ? updated : button)) });

      return (
        <>
          {buttons.map((button, index) => (
            <div key={index} className={styles.buttonRow}>
              <input
                type="text"
                className={`${styles.field} ${styles.buttonLabel} body-sm-regular`}
                value={button.label}
                onChange={(e) => setButton(index, { ...button, label: e.target.value })}
                placeholder={t('systemMessages.editor.buttonLabelPlaceholder')}
              />
              <input
                type="text"
                className={`${styles.field} ${styles.buttonUrl} body-sm-regular`}
                value={button.url}
                onChange={(e) => setButton(index, { ...button, url: e.target.value })}
                placeholder={t('systemMessages.editor.buttonUrlPlaceholder')}
                dir="ltr"
              />
              <select
                className={`${styles.field} ${styles.buttonVariant} body-sm-regular`}
                value={button.variant}
                onChange={(e) => setButton(index, { ...button, variant: e.target.value as SystemMessageButton['variant'] })}
              >
                <option value="primary">{t('systemMessages.editor.buttonPrimary')}</option>
                <option value="secondary">{t('systemMessages.editor.buttonSecondary')}</option>
              </select>
              <button
                type="button"
                className={`${styles.toolButton} ${styles.removeButton}`}
                onClick={() => onChange({ ...block, buttons: buttons.filter((_, i) => i !== index) })}
                aria-label={t('systemMessages.editor.removeButton')}
              >
                <TrashGlyph />
              </button>
            </div>
          ))}
          <button
            type="button"
            className={`${styles.addButton} body-sm-semibold`}
            onClick={() => onChange({ ...block, buttons: [...buttons, { label: '', url: '', variant: 'secondary' }] })}
            disabled={buttons.length >= MAX_BUTTONS}
          >
            <PlusGlyph />
            {t('systemMessages.editor.addButton')}
          </button>
          <p className="body-xs-sm-regular text-(--font-body-3)">{t('systemMessages.editor.buttonUrlHint')}</p>
        </>
      );
    }

    case 'image':
      // Not addable here: a screenshot comes with the feature's PR, taken against demo data. An admin
      // can describe it or remove it (HAT-1489).
      return (
        <>
          {block.src && <img className={styles.imagePreview} src={block.src} alt="" />}
          <input
            type="text"
            className={`${styles.field} body-sm-regular`}
            value={block.text ?? ''}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            placeholder={t('systemMessages.editor.imageAlt')}
            aria-label={t('systemMessages.editor.imageAlt')}
          />
        </>
      );

    case 'callout':
    case 'paragraph':
    default:
      return (
        <textarea
          rows={block.kind === 'callout' ? 2 : 3}
          className={`${styles.field} body-sm-regular`}
          value={block.text ?? ''}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          placeholder={t(block.kind === 'callout' ? 'systemMessages.editor.calloutPlaceholder' : 'systemMessages.editor.paragraphPlaceholder')}
        />
      );
  }
};

const Chevron = ({ direction }: { direction: 'up' | 'down' }) => (
  <svg
    className={styles.toolIcon}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
    style={direction === 'down' ? { transform: 'rotate(180deg)' } : undefined}
  >
    <path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashGlyph = () => (
  <svg className={styles.toolIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 12.5h9L17.5 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusGlyph = () => (
  <svg className={styles.toolIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M12 5.5v13M5.5 12h13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

export default SystemMessageBlocksEditor;
